/**
 * IntakePatientTable Component
 *
 * Patient-centric table showing unassigned intake patients
 * with collapsible rows for provider recommendations
 */

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  IconButton,
  Collapse,
  Box,
} from '@mui/material';
import { useState, Fragment } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { IntakePatient, ProviderRecommendation, AlternativeAssignmentOptions } from '../types/intake.types';
import ProviderRecommendationRow from './ProviderRecommendationRow';

interface IntakePatientTableProps {
  patients: IntakePatient[];
  expandedPatientIds: Set<number>;
  onSelectPatient: (patientId: number) => void;
  recommendations: { [patientId: number]: ProviderRecommendation[] };
  alternatives: { [patientId: number]: AlternativeAssignmentOptions };
  targetUtilization: number;
  onAssign: (patientId: number, providerId: number, providerName: string) => void;
  onAssignAlternative: (patientId: number, date: Date, providerName?: string) => void;
  onAssignNextBest: (patientId: number, providerId: number, providerName: string) => void;
  onCreateProvisional: (patientId: number, date: Date) => void;
}

type SortField = 'patientName' | 'expectedDischargeDate' | 'intakeVisitDate' | 'priority' | 'role';
type SortDirection = 'asc' | 'desc';

export default function IntakePatientTable({
  patients,
  expandedPatientIds,
  onSelectPatient,
  recommendations,
  alternatives,
  targetUtilization,
  onAssign,
  onAssignAlternative,
  onAssignNextBest,
  onCreateProvisional,
}: IntakePatientTableProps) {
  const [sortField, setSortField] = useState<SortField>('priority');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedPatients = [...patients].sort((a, b) => {
    let comparison = 0;

    switch (sortField) {
      case 'patientName':
        comparison = a.patientName.localeCompare(b.patientName);
        break;
      case 'expectedDischargeDate':
        comparison = a.expectedDischargeDate.getTime() - b.expectedDischargeDate.getTime();
        break;
      case 'intakeVisitDate':
        comparison = a.intakeVisitDate.getTime() - b.intakeVisitDate.getTime();
        break;
      case 'priority':
        const priorityOrder = { HIGH: 3, MEDIUM: 2, LOW: 1 };
        comparison = priorityOrder[a.priority] - priorityOrder[b.priority];
        break;
      case 'role':
        comparison = a.requiredRole.localeCompare(b.requiredRole);
        break;
    }

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width={50} />
            <TableCell>
              <TableSortLabel
                active={sortField === 'patientName'}
                direction={sortField === 'patientName' ? sortDirection : 'asc'}
                onClick={() => handleSort('patientName')}
              >
                Patient Name
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'expectedDischargeDate'}
                direction={sortField === 'expectedDischargeDate' ? sortDirection : 'asc'}
                onClick={() => handleSort('expectedDischargeDate')}
              >
                Expected Discharge Date
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'intakeVisitDate'}
                direction={sortField === 'intakeVisitDate' ? sortDirection : 'asc'}
                onClick={() => handleSort('intakeVisitDate')}
              >
                Intake Visit Due
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'priority'}
                direction={sortField === 'priority' ? sortDirection : 'asc'}
                onClick={() => handleSort('priority')}
              >
                Priority
              </TableSortLabel>
            </TableCell>
            <TableCell>Territory</TableCell>
            <TableCell>Visit Cadence</TableCell>
            <TableCell>LOS</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedPatients.map((patient) => {
            const isExpanded = expandedPatientIds.has(patient.id);
            const patientRecommendations = recommendations[patient.id] || [];
            const patientAlternatives = alternatives[patient.id];

            return (
              <Fragment key={patient.id}>
                <TableRow
                  hover
                  selected={isExpanded}
                  onClick={() => onSelectPatient(patient.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <IconButton
                      size="small"
                    >
                      {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <strong>{patient.patientName}</strong>
                  </TableCell>
                  <TableCell>{patient.expectedDischargeDate.toLocaleDateString()}</TableCell>
                  <TableCell>{patient.intakeVisitDate.toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip
                      label={patient.priority}
                      size="small"
                      sx={{
                        bgcolor:
                          patient.priority === 'HIGH'
                            ? '#ffebee' // Light red
                            : patient.priority === 'MEDIUM'
                            ? '#fff3e0' // Light orange
                            : '#f5f5f5', // Light grey
                        color:
                          patient.priority === 'HIGH'
                            ? '#c62828' // Dark red
                            : patient.priority === 'MEDIUM'
                            ? '#e65100' // Dark orange
                            : '#616161', // Dark grey
                        border: '1.5px solid',
                        borderColor:
                          patient.priority === 'HIGH'
                            ? '#ef5350' // Medium red
                            : patient.priority === 'MEDIUM'
                            ? '#ff9800' // Medium orange
                            : '#bdbdbd', // Medium grey
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>{patient.territory}</TableCell>
                  <TableCell>{patient.expectedVisitsPerWeek}x/week</TableCell>
                  <TableCell>{patient.expectedDurationWeeks} weeks</TableCell>
                  <TableCell>
                    {patient.status === 'assigned' ? (
                      <Chip
                        label="Assigned"
                        size="small"
                        sx={{
                          bgcolor: '#e8f5e9', // Light green
                          color: '#2e7d32', // Dark green
                          border: '1.5px solid',
                          borderColor: '#66bb6a', // Medium green
                          fontWeight: 600,
                        }}
                      />
                    ) : (
                      <Chip
                        label="Unassigned"
                        size="small"
                        sx={{
                          bgcolor: '#f5f5f5', // Light grey
                          color: '#616161', // Dark grey
                          border: '1.5px solid',
                          borderColor: '#bdbdbd', // Medium grey
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 2 }}>
                        <ProviderRecommendationRow
                          patientId={patient.id}
                          patientName={patient.patientName}
                          intakeDate={patient.intakeVisitDate}
                          recommendations={patientRecommendations}
                          alternatives={patientAlternatives}
                          targetUtilization={targetUtilization}
                          onAssign={(providerId, providerName) =>
                            onAssign(patient.id, providerId, providerName)
                          }
                          onAssignAlternative={(date, providerName) =>
                            onAssignAlternative(patient.id, date, providerName)
                          }
                          onAssignNextBest={(providerId, providerName) =>
                            onAssignNextBest(patient.id, providerId, providerName)
                          }
                          onCreateProvisional={(date) =>
                            onCreateProvisional(patient.id, date)
                          }
                        />
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
